import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Calendar as CalendarIcon,
  Wrench,
  Plus,
  Trash2,
  Edit3,
  LayoutDashboard,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Settings as SettingsIcon,
  CalendarDays,
  KeyRound,
  Upload,
  Image as ImageIcon,
  Volume2,
  VolumeX,
} from "lucide-react";
import { api, clearToken, getToken, saveToken, formatError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { CalendarOff, Check, X } from "lucide-react";

const STATUS_LABEL = {
  pending: "Ausstehend",
  confirmed: "Bestätigt",
  completed: "Erledigt",
  cancelled: "Storniert",
};
const STATUS_COLOR = {
  pending: "text-[#F59E0B] border-[#F59E0B]",
  confirmed: "text-[#3B82F6] border-[#3B82F6]",
  completed: "text-[#10B981] border-[#10B981]",
  cancelled: "text-white/40 border-white/20",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    refreshAll();
  }, []);

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [s, b, sv] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/services"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setServices(sv.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearToken();
        nav("/admin/login");
      } else {
        toast.error(formatError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Status: ${STATUS_LABEL[status]}`);
      refreshAll();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const confirmBooking = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/confirm`);
      toast.success("Termin bestätigt – Kunde wurde per Mail informiert");
      refreshAll();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const declineBooking = async (id, reason) => {
    try {
      await api.post(`/admin/bookings/${id}/decline`, { reason });
      toast.success("Termin abgesagt – Kunde wurde per Mail informiert");
      refreshAll();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Buchung wirklich löschen?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Buchung gelöscht");
      refreshAll();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const filtered = filterStatus
    ? bookings.filter((b) => b.status === filterStatus)
    : bookings;

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-shrink-0 flex-col border-r border-white/10 bg-[#09090B] p-6 md:flex">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center bg-[#FF4B00]">
            <Wrench className="h-5 w-5 text-white" />
          </div>
          <span className="font-[Chivo] text-sm font-black uppercase tracking-widest">
            Admin
          </span>
        </Link>

        <div className="mono mt-10 mb-3 text-[10px] uppercase tracking-[0.2em] text-white/40">
          Verwaltung
        </div>
        <nav className="flex flex-col gap-1">
          <NavBtn
            active={tab === "bookings"}
            onClick={() => setTab("bookings")}
            icon={<CalendarIcon className="h-4 w-4" strokeWidth={1.5} />}
            testId="tab-bookings"
          >
            Buchungen
          </NavBtn>
          <NavBtn
            active={tab === "calendar"}
            onClick={() => setTab("calendar")}
            icon={<CalendarDays className="h-4 w-4" strokeWidth={1.5} />}
            testId="tab-calendar"
          >
            Kalender
          </NavBtn>
          <NavBtn
            active={tab === "blocks"}
            onClick={() => setTab("blocks")}
            icon={<CalendarOff className="h-4 w-4" strokeWidth={1.5} />}
            testId="tab-blocks"
          >
            Kalender-Sperren
          </NavBtn>
          <NavBtn
            active={tab === "services"}
            onClick={() => setTab("services")}
            icon={<LayoutDashboard className="h-4 w-4" strokeWidth={1.5} />}
            testId="tab-services"
          >
            Leistungen
          </NavBtn>
          <NavBtn
            active={tab === "settings"}
            onClick={() => setTab("settings")}
            icon={<SettingsIcon className="h-4 w-4" strokeWidth={1.5} />}
            testId="tab-settings"
          >
            Einstellungen
          </NavBtn>
          <NavBtn
            active={tab === "credentials"}
            onClick={() => setTab("credentials")}
            icon={<KeyRound className="h-4 w-4" strokeWidth={1.5} />}
            testId="tab-credentials"
          >
            Zugangsdaten
          </NavBtn>
        </nav>

        <button
          data-testid="logout-btn"
          onClick={logout}
          className="mono mt-auto inline-flex items-center gap-2 border border-white/10 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-white/60 hover:border-white/40 hover:text-white"
        >
          <LogOut className="h-3.5 w-3.5" />
          Abmelden
        </button>
      </aside>

      {/* Content */}
      <main className="flex-1 bg-[#0a0a0c] p-6 lg:p-10">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <span className="tag-orange">Dashboard</span>
            <h1 className="mt-4 text-3xl font-black uppercase">
              {tab === "bookings"
                ? "Buchungen"
                : tab === "calendar"
                  ? "Wochenkalender"
                  : tab === "blocks"
                    ? "Kalender-Sperren"
                    : tab === "services"
                      ? "Leistungen"
                      : tab === "credentials"
                        ? "Zugangsdaten"
                        : "Werkstatt-Einstellungen"}
            </h1>
          </div>
          <button
            data-testid="logout-mobile"
            onClick={logout}
            className="md:hidden inline-flex items-center gap-2 border border-white/20 px-3 py-2 text-xs uppercase"
          >
            <LogOut className="h-3.5 w-3.5" /> Abmelden
          </button>
        </div>

        {/* Mobile Tab Strip */}
        <div
          data-testid="mobile-tabs"
          className="md:hidden mb-6 -mx-6 flex gap-2 overflow-x-auto border-b border-white/10 bg-[#09090B] px-6 py-3"
        >
          {[
            { key: "bookings", label: "Buchungen", icon: CalendarIcon },
            { key: "calendar", label: "Kalender", icon: CalendarDays },
            { key: "blocks", label: "Sperren", icon: CalendarOff },
            { key: "services", label: "Leistungen", icon: LayoutDashboard },
            { key: "settings", label: "Einstellungen", icon: SettingsIcon },
            { key: "credentials", label: "Zugangsdaten", icon: KeyRound },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`mobile-tab-${key}`}
              onClick={() => setTab(key)}
              className={`mono inline-flex flex-shrink-0 items-center gap-2 border px-3 py-2 text-[10px] uppercase tracking-widest ${
                tab === key
                  ? "border-[#FF4B00] bg-[#FF4B00]/10 text-[#FF4B00]"
                  : "border-white/10 text-white/60"
              }`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
              {label}
            </button>
          ))}
        </div>

        {/* Stats */}
        {stats && tab === "bookings" && (
          <div className="mb-8 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-5">
            <StatCard label="Heute" value={stats.today} accent />
            <StatCard label="Ausstehend" value={stats.pending} />
            <StatCard label="Bestätigt" value={stats.confirmed} />
            <StatCard label="Erledigt" value={stats.completed} />
            <StatCard label="Gesamt" value={stats.total} />
          </div>
        )}

        {loading && <p className="text-sm text-white/50">Lädt...</p>}

        {tab === "bookings" && !loading && (
          <BookingsView
            bookings={filtered}
            allBookings={bookings}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            updateStatus={updateStatus}
            deleteBooking={deleteBooking}
            confirmBooking={confirmBooking}
            declineBooking={declineBooking}
          />
        )}

        {tab === "blocks" && !loading && <TimeBlocksView />}

        {tab === "calendar" && !loading && (
          <CalendarView bookings={bookings} updateStatus={updateStatus} />
        )}

        {tab === "services" && !loading && (
          <ServicesView services={services} refresh={refreshAll} />
        )}

        {tab === "settings" && !loading && <SettingsView />}

        {tab === "credentials" && !loading && <CredentialsView />}
      </main>
    </div>
  );
}

function NavBtn({ active, onClick, icon, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
        active
          ? "bg-[#FF4B00]/10 text-white border-l-2 border-[#FF4B00]"
          : "text-white/60 hover:text-white border-l-2 border-transparent"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-[#0d0d10] p-6">
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/50">
        {label}
      </div>
      <div
        className={`mono mt-3 text-3xl font-bold ${accent ? "text-[#FF4B00]" : "text-white"}`}
      >
        {value}
      </div>
    </div>
  );
}

function BookingsView({
  bookings,
  filterStatus,
  setFilterStatus,
  updateStatus,
  deleteBooking,
  confirmBooking,
  declineBooking,
}) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          Filter:
        </span>
        {["", "pending", "confirmed", "completed", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            data-testid={`filter-${s || "all"}`}
            onClick={() => setFilterStatus(s)}
            className={`mono border px-3 py-1 text-[10px] uppercase tracking-widest ${
              filterStatus === s
                ? "border-[#FF4B00] text-[#FF4B00]"
                : "border-white/10 text-white/50 hover:border-white/30"
            }`}
          >
            {s ? STATUS_LABEL[s] : "Alle"}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto border border-white/10">
        <table className="min-w-full">
          <thead className="bg-[#0d0d10]">
            <tr>
              {[
                "Ref",
                "Datum",
                "Zeit",
                "Kunde",
                "Fahrzeug",
                "Leistung",
                "Status",
                "",
              ].map((h) => (
                <th
                  key={h}
                  className="mono px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] text-white/50"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="p-8 text-center text-sm text-white/50"
                >
                  Keine Buchungen gefunden.
                </td>
              </tr>
            )}
            {bookings.map((b, i) => (
              <tr
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className={i % 2 ? "bg-[#0d0d10]" : "bg-[#09090B]"}
              >
                <td className="mono px-4 py-3 text-xs text-[#FF4B00]">
                  {b.reference}
                </td>
                <td className="mono px-4 py-3 text-xs text-white">{b.date}</td>
                <td className="mono px-4 py-3 text-xs text-white">{b.time}</td>
                <td className="px-4 py-3 text-sm">
                  <div className="text-white">{b.customer_name}</div>
                  <div className="mono text-[10px] text-white/50">
                    {b.customer_phone}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="text-white">
                    {b.vehicle_make} {b.vehicle_model}
                  </div>
                  <div className="mono text-[10px] text-white/50">
                    {b.license_plate}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-white/80">
                  {b.service_name}
                </td>
                <td className="px-4 py-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`status-btn-${b.id}`}
                        className={`mono inline-flex items-center gap-1 border px-2 py-1 text-[10px] uppercase tracking-widest ${STATUS_COLOR[b.status]}`}
                      >
                        {STATUS_LABEL[b.status]}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="border-white/10 bg-[#0d0d10]">
                      {Object.keys(STATUS_LABEL).map((s) => (
                        <DropdownMenuItem
                          key={s}
                          data-testid={`status-set-${b.id}-${s}`}
                          onClick={() => updateStatus(b.id, s)}
                        >
                          {STATUS_LABEL[s]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {b.status === "pending" && (
                      <>
                        <button
                          data-testid={`confirm-booking-${b.id}`}
                          onClick={() => confirmBooking(b.id)}
                          title="Bestätigen – Kunde bekommt Bestätigungs-Mail"
                          className="inline-flex items-center justify-center border border-[#10B981] px-2 py-1 text-[#10B981] transition hover:bg-[#10B981] hover:text-white"
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </button>
                        <button
                          data-testid={`decline-booking-${b.id}`}
                          onClick={() => {
                            const reason = window.prompt(
                              "Optionale Begründung für den Kunden (leer lassen für keine):",
                              "",
                            );
                            if (reason === null) return;
                            declineBooking(b.id, reason || "");
                          }}
                          title="Ablehnen – Kunde bekommt Absage-Mail"
                          className="inline-flex items-center justify-center border border-[#EF4444] px-2 py-1 text-[#EF4444] transition hover:bg-[#EF4444] hover:text-white"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </button>
                      </>
                    )}
                    <button
                      data-testid={`delete-booking-${b.id}`}
                      onClick={() => deleteBooking(b.id)}
                      className="ml-1 text-white/40 hover:text-[#EF4444]"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_SERVICE = {
  name: "",
  description: "",
  name_en: "",
  description_en: "",
  duration_minutes: 60,
  price_eur: 0,
  active: true,
};

const SETTINGS_FIELDS = [
  { key: "workshop_name", label: "Werkstatt-Name", type: "text" },
  { key: "tagline", label: "Untertitel", type: "text" },
  { key: "address_line1", label: "Straße & Hausnummer", type: "text" },
  { key: "address_line2", label: "PLZ & Ort", type: "text" },
  { key: "phone", label: "Telefon", type: "text" },
  { key: "email", label: "E-Mail", type: "email" },
  { key: "hours_weekdays", label: "Öffnungszeiten Mo–Sa", type: "text" },
  { key: "hours_sunday", label: "Öffnungszeiten Sonntag", type: "text" },
  { key: "footer_note", label: "Fußzeilen-Beschreibung", type: "textarea" },
  {
    key: "notification_email",
    label: "Benachrichtigungs-E-Mail (Admin)",
    type: "email",
    hint: "An diese Adresse werden neue Buchungen gemeldet. Leer lassen = Kontakt-E-Mail wird verwendet.",
  },
  {
    key: "slogan",
    label: "Slogan (im Hero)",
    type: "text",
    hint: "Erscheint oberhalb des Werkstatt-Namens auf der Startseite.",
  },
  {
    key: "hero_description",
    label: "Hero-Beschreibung",
    type: "textarea",
    hint: "Der Fließtext unterhalb des Werkstatt-Namens auf der Startseite.",
  },
  {
    key: "tagline_bottom",
    label: "Untere Marken-Zeile",
    type: "text",
    hint: 'Auffälliges Motto (z.B. "Dein Auto. Unsere Passion."). Erscheint als Banner vor dem Footer.',
  },
  {
    key: "instagram_handle",
    label: "Instagram-Handle",
    type: "text",
    hint: "Ohne @ (z.B. ogs__garage). Leer lassen = kein Instagram-Link.",
  },
  {
    key: "copyright_text",
    label: "Copyright-Zeile (Fußzeile ganz unten)",
    type: "text",
    hint: 'Steht ganz unten in der Fußzeile (z.B. "© 2026 OG\'s Garage · Alle Rechte vorbehalten").',
  },
  {
    key: "copyright_text_en",
    label: "Copyright-Zeile (English)",
    type: "text",
    hint: "Englische Version. Leer lassen = deutsche Version wird auch auf EN gezeigt.",
  },
  {
    key: "tagline_en",
    label: "Untertitel (English)",
    type: "text",
    hint: "Englische Version des Untertitels. Leer lassen = keine EN-Übersetzung.",
  },
  {
    key: "slogan_en",
    label: "Slogan (English)",
    type: "text",
    hint: "Englische Version des Slogans. Leer lassen = DE wird auch bei EN gezeigt.",
  },
  {
    key: "hero_description_en",
    label: "Hero-Beschreibung (English)",
    type: "textarea",
    hint: "Englische Version. Leer lassen = deutsche Version.",
  },
  {
    key: "tagline_bottom_en",
    label: "Untere Marken-Zeile (English)",
    type: "text",
    hint: 'Englische Version des Mottos (z.B. "Your car. Our passion.").',
  },
  {
    key: "footer_note_en",
    label: "Fußzeilen-Beschreibung (English)",
    type: "textarea",
    hint: "Englische Version. Leer lassen = deutsche Version.",
  },
  {
    key: "promo_sticker_headline_en",
    label: "Aktions-Sticker Überschrift (English)",
    type: "text",
    hint: "Englische Version des Sticker-Titels.",
  },
  {
    key: "promo_sticker_subline_en",
    label: "Aktions-Sticker Zusatzzeile (English)",
    type: "text",
    hint: "Englische Version der Sticker-Unterzeile.",
  },
  {
    key: "imprint_additional_en",
    label: "Impressum · Zusatztext (English)",
    type: "textarea",
    hint: "Englische Version der zusätzlichen Angaben.",
  },
  {
    key: "imprint_owner",
    label: "Impressum · Inhaber / Geschäftsführer",
    type: "text",
    hint: "Voller Name des Inhabers oder Geschäftsführers (Pflichtangabe).",
  },
  {
    key: "imprint_responsible",
    label: "Impressum · Verantwortlich für den Inhalt",
    type: "text",
    hint: "Verantwortlich gemäß § 18 Abs. 2 MStV (meist gleiche Person wie Inhaber).",
  },
  {
    key: "imprint_ustid",
    label: "Impressum · Umsatzsteuer-ID",
    type: "text",
    hint: 'Falls vorhanden (z.B. "DE123456789"). Leer lassen, wenn Kleinunternehmer.',
  },
  {
    key: "imprint_register",
    label: "Impressum · Handelsregister / Gewerbe",
    type: "text",
    hint: 'Optional, z.B. "Amtsgericht Stuttgart, HRB 123456" oder "Gewerbeanmeldung Weil im Schönbuch".',
  },
  {
    key: "imprint_additional",
    label: "Impressum · Zusätzlicher Text",
    type: "textarea",
    hint: "Optional. Weitere Angaben wie Aufsichtsbehörde, Kammer, Berufshaftpflicht usw.",
  },
  {
    key: "promo_sticker_headline",
    label: "Aktions-Sticker: Überschrift",
    type: "text",
    hint: 'Kurz & knackig (z.B. "-10% Rabatt", "Gratis-Check").',
  },
  {
    key: "promo_sticker_subline",
    label: "Aktions-Sticker: Zusatzzeile",
    type: "text",
    hint: 'Kleinere Zeile darunter (z.B. "Beim ersten Termin", "Nur diesen Monat").',
  },
];

function TimeBlocksView() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    all_day: true,
    start_time: "",
    end_time: "",
    reason: "",
  });

  const load = async () => {
    try {
      const r = await api.get("/admin/time-blocks");
      setBlocks(r.data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Start- und Enddatum sind Pflicht");
      return;
    }
    const payload = {
      start_date: form.start_date,
      end_date: form.end_date,
      reason: form.reason || "",
    };
    if (!form.all_day) {
      if (!form.start_time || !form.end_time) {
        toast.error("Bei Stunden-Sperren bitte Start- und Endzeit angeben");
        return;
      }
      payload.start_time = form.start_time;
      payload.end_time = form.end_time;
    }
    try {
      await api.post("/admin/time-blocks", payload);
      toast.success("Sperre gespeichert");
      setForm({
        start_date: "",
        end_date: "",
        all_day: true,
        start_time: "",
        end_time: "",
        reason: "",
      });
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sperre wirklich entfernen?")) return;
    try {
      await api.delete(`/admin/time-blocks/${id}`);
      toast.success("Sperre entfernt");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="time-blocks-view" className="space-y-8">
      <div className="border border-white/10 bg-[#0d0d10] p-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-white">
          Neue Sperre hinzufügen
        </h3>
        <p className="mt-1 text-xs text-white/50">
          Kunden können die gesperrten Zeitfenster nicht mehr buchen.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
              Von (Datum) *
            </Label>
            <Input
              type="date"
              data-testid="block-start-date"
              className="mono mt-2 border-white/20 bg-black text-white"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />
          </div>
          <div>
            <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
              Bis (Datum) *
            </Label>
            <Input
              type="date"
              data-testid="block-end-date"
              className="mono mt-2 border-white/20 bg-black text-white"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Switch
            data-testid="block-all-day"
            checked={form.all_day}
            onCheckedChange={(v) => setForm({ ...form, all_day: v })}
          />
          <Label className="text-sm text-white">
            Ganztägig sperren (kein Zeitfenster)
          </Label>
        </div>

        {!form.all_day && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                Von (Uhrzeit)
              </Label>
              <Input
                type="time"
                data-testid="block-start-time"
                className="mono mt-2 border-white/20 bg-black text-white"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
              />
            </div>
            <div>
              <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                Bis (Uhrzeit)
              </Label>
              <Input
                type="time"
                data-testid="block-end-time"
                className="mono mt-2 border-white/20 bg-black text-white"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
              />
            </div>
          </div>
        )}

        <div className="mt-4">
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Grund (optional)
          </Label>
          <Input
            data-testid="block-reason"
            className="mt-2 border-white/20 bg-black text-white"
            placeholder="z.B. Urlaub, Weiterbildung, Feiertag"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </div>

        <button
          data-testid="save-block"
          onClick={save}
          className="mt-6 inline-flex items-center gap-2 bg-[#FF4B00] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition hover:bg-[#E64300]"
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} /> Sperre speichern
        </button>
      </div>

      <div>
        <h3 className="mb-4 text-sm font-bold uppercase tracking-widest text-white">
          Aktive Sperren
        </h3>
        {loading && <p className="text-sm text-white/50">Lädt...</p>}
        {!loading && blocks.length === 0 && (
          <p className="text-sm text-white/50">Noch keine Sperren angelegt.</p>
        )}
        {!loading && blocks.length > 0 && (
          <div className="overflow-x-auto border border-white/10">
            <table className="min-w-full">
              <thead className="bg-[#0d0d10]">
                <tr>
                  {["Zeitraum", "Uhrzeit", "Grund", ""].map((h) => (
                    <th
                      key={h}
                      className="mono px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] text-white/50"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blocks.map((b, i) => (
                  <tr
                    key={b.id}
                    data-testid={`block-row-${b.id}`}
                    className={i % 2 ? "bg-[#0d0d10]" : "bg-[#09090B]"}
                  >
                    <td className="mono px-4 py-3 text-xs text-white">
                      {b.start_date === b.end_date
                        ? b.start_date
                        : `${b.start_date} – ${b.end_date}`}
                    </td>
                    <td className="mono px-4 py-3 text-xs text-white/70">
                      {b.start_time && b.end_time
                        ? `${b.start_time} – ${b.end_time}`
                        : "Ganztägig"}
                    </td>
                    <td className="px-4 py-3 text-sm text-white/80">
                      {b.reason || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`delete-block-${b.id}`}
                        onClick={() => remove(b.id)}
                        className="text-white/40 hover:text-[#EF4444]"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CredentialsView() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setNewEmail(r.data.email))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Neues Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/auth/change-credentials", {
        current_password: currentPassword,
        new_email: newEmail,
        new_password: newPassword,
      });
      saveToken(r.data.token);
      toast.success("Zugangsdaten aktualisiert");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="credentials-view" className="max-w-2xl">
      <p className="mb-8 text-sm text-white/60">
        Ändern Sie hier Ihre Admin-E-Mail und Ihr Passwort. Nach dem Speichern
        bleiben Sie mit den neuen Daten angemeldet.
      </p>

      <form
        onSubmit={submit}
        className="space-y-5 border border-white/10 bg-[#0d0d10] p-6 sm:p-8"
      >
        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Aktuelles Passwort *
          </Label>
          <Input
            data-testid="cred-current-password"
            type="password"
            className="mono mt-2 border-white/20 bg-black text-white"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>

        <div className="h-px bg-white/10" />

        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Neue E-Mail *
          </Label>
          <Input
            data-testid="cred-new-email"
            type="email"
            className="mono mt-2 border-white/20 bg-black text-white"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
          />
        </div>

        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Neues Passwort *
          </Label>
          <Input
            data-testid="cred-new-password"
            type="password"
            className="mono mt-2 border-white/20 bg-black text-white"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
          <p className="mono mt-2 text-[10px] uppercase tracking-widest text-white/40">
            Mindestens 6 Zeichen
          </p>
        </div>

        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Neues Passwort bestätigen *
          </Label>
          <Input
            data-testid="cred-confirm-password"
            type="password"
            className="mono mt-2 border-white/20 bg-black text-white"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            data-testid="cred-submit"
            type="submit"
            disabled={saving}
            className="bg-[#FF4B00] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300] disabled:opacity-40"
          >
            {saving ? "Speichern..." : "Zugangsdaten aktualisieren"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CalendarView({ bookings, updateStatus }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay(); // 0 = Sun
    const diff = day === 0 ? -6 : 1 - day; // shift to Monday
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const days = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const fmtDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const dayLabels = ["Mo", "Di", "Mi", "Do", "Fr"];
  const OPEN = 8, CLOSE = 17;
  const SLOT_H = 32; // px per 30min slot
  const totalMin = (CLOSE - OPEN) * 60;
  const totalH = (totalMin / 30) * SLOT_H;

  const timeRows = [];
  for (let h = OPEN; h < CLOSE; h++) {
    timeRows.push(`${String(h).padStart(2, "0")}:00`);
  }

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const weekLabel = `${days[0].toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} – ${days[4].toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}`;

  const STATUS_BG = {
    pending: "bg-[#F59E0B]/15 border-[#F59E0B] text-[#F59E0B]",
    confirmed: "bg-[#3B82F6]/15 border-[#3B82F6] text-[#3B82F6]",
    completed: "bg-[#10B981]/15 border-[#10B981] text-[#10B981]",
    cancelled: "bg-white/5 border-white/20 text-white/40 line-through",
  };

  return (
    <div data-testid="calendar-view">
      <div className="mb-6 flex items-center justify-between border border-white/10 bg-[#0d0d10] p-4">
        <button
          data-testid="cal-prev"
          onClick={() => shiftWeek(-1)}
          className="mono flex items-center gap-2 border border-white/10 px-3 py-2 text-[10px] uppercase tracking-widest hover:border-white/40"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Woche
        </button>
        <div className="mono text-sm uppercase tracking-[0.15em] text-white">
          {weekLabel}
        </div>
        <button
          data-testid="cal-next"
          onClick={() => shiftWeek(1)}
          className="mono flex items-center gap-2 border border-white/10 px-3 py-2 text-[10px] uppercase tracking-widest hover:border-white/40"
        >
          Woche <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="overflow-x-auto border border-white/10 bg-[#09090B]">
        <div className="grid" style={{ gridTemplateColumns: "70px repeat(5, minmax(160px, 1fr))" }}>
          {/* Header row */}
          <div className="border-b border-r border-white/10 bg-[#0d0d10]" />
          {days.map((d, i) => {
            const isToday = fmtDate(d) === fmtDate(new Date());
            return (
              <div
                key={i}
                className={`border-b border-r border-white/10 p-3 text-center last:border-r-0 ${isToday ? "bg-[#FF4B00]/10" : "bg-[#0d0d10]"}`}
              >
                <div className={`mono text-[10px] uppercase tracking-[0.2em] ${isToday ? "text-[#FF4B00]" : "text-white/50"}`}>
                  {dayLabels[i]}
                </div>
                <div className="mono mt-1 text-lg font-bold text-white">
                  {String(d.getDate()).padStart(2, "0")}.{String(d.getMonth() + 1).padStart(2, "0")}
                </div>
              </div>
            );
          })}

          {/* Time gutter */}
          <div className="relative border-r border-white/10" style={{ height: totalH }}>
            {timeRows.map((t, i) => (
              <div
                key={t}
                className="mono absolute left-0 right-0 border-b border-white/5 pl-2 text-[10px] text-white/40"
                style={{ top: i * 2 * SLOT_H, height: 2 * SLOT_H }}
              >
                {t}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, di) => {
            const ds = fmtDate(day);
            const dayBookings = bookings.filter((b) => b.date === ds);
            return (
              <div
                key={di}
                className="relative border-r border-white/10 last:border-r-0"
                style={{ height: totalH }}
                data-testid={`cal-col-${ds}`}
              >
                {/* Half-hour gridlines */}
                {Array.from({ length: (CLOSE - OPEN) * 2 }).map((_, i) => (
                  <div
                    key={i}
                    className={`absolute left-0 right-0 ${i % 2 === 0 ? "border-t border-white/10" : "border-t border-white/[0.04]"}`}
                    style={{ top: i * SLOT_H, height: SLOT_H }}
                  />
                ))}

                {/* Booking blocks */}
                {dayBookings.map((b) => {
                  const [bh, bm] = b.time.split(":").map(Number);
                  const startMin = bh * 60 + bm - OPEN * 60;
                  const top = (startMin / 30) * SLOT_H;
                  const height = Math.max((b.duration_minutes / 30) * SLOT_H, 24);
                  return (
                    <DropdownMenu key={b.id}>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`cal-booking-${b.id}`}
                          className={`absolute left-1 right-1 z-10 overflow-hidden border-l-2 p-2 text-left transition-transform hover:scale-[1.02] ${STATUS_BG[b.status]}`}
                          style={{ top, height }}
                        >
                          <div className="mono text-[9px] font-bold uppercase tracking-widest">
                            {b.time}
                          </div>
                          <div className="mt-0.5 truncate text-xs font-bold text-white">
                            {b.customer_name}
                          </div>
                          <div className="mono truncate text-[10px] text-white/70">
                            {b.service_name}
                          </div>
                          <div className="mono mt-0.5 truncate text-[9px] text-white/50">
                            {b.license_plate}
                          </div>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="border-white/10 bg-[#0d0d10]">
                        <div className="border-b border-white/10 px-2 py-2">
                          <div className="mono text-[9px] uppercase tracking-widest text-white/40">
                            {b.reference}
                          </div>
                          <div className="mono text-[10px] text-white/60">
                            {b.customer_phone}
                          </div>
                        </div>
                        {Object.keys(STATUS_LABEL).map((s) => (
                          <DropdownMenuItem
                            key={s}
                            onClick={() => updateStatus(b.id, s)}
                          >
                            {STATUS_LABEL[s]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          Status:
        </span>
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <span
            key={k}
            className={`mono border-l-2 px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_BG[k]}`}
          >
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

function SettingsView() {
  const current = useSettings();
  const qc = useQueryClient();
  const [form, setForm] = useState(current);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm(current);
  }, [current]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 2 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, logo_url: r.data.logo_url }));
      await qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Logo hochgeladen");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = () => {
    setForm((prev) => ({ ...prev, logo_url: "", logo_storage_path: "" }));
  };

  const heroCarInputRef = useRef(null);
  const [uploadingCar, setUploadingCar] = useState(false);

  const uploadHeroCar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 4 MB)");
      return;
    }
    setUploadingCar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-hero-car", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, hero_car_url: r.data.hero_car_url }));
      await qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Fahrzeug-Bild hochgeladen");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploadingCar(false);
      if (heroCarInputRef.current) heroCarInputRef.current.value = "";
    }
  };

  const removeHeroCar = () => {
    setForm((prev) => ({ ...prev, hero_car_url: "", hero_car_storage_path: "" }));
  };

  const heroSoundInputRef = useRef(null);
  const [uploadingSound, setUploadingSound] = useState(false);

  const uploadHeroSound = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 5 MB)");
      return;
    }
    setUploadingSound(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-hero-sound", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, hero_sound_url: r.data.hero_sound_url }));
      await qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Sound hochgeladen");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploadingSound(false);
      if (heroSoundInputRef.current) heroSoundInputRef.current.value = "";
    }
  };

  const removeHeroSound = () => {
    setForm((prev) => ({ ...prev, hero_sound_url: "", hero_sound_storage_path: "" }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-view" className="max-w-3xl">
      <p className="mb-8 text-sm text-white/60">
        Diese Angaben erscheinen im Header, Footer und auf allen Kontaktflächen
        der Website.
      </p>

      <div className="grid gap-5 border border-white/10 bg-[#0d0d10] p-6 sm:p-8">
        {/* Logo Upload */}
        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Logo
          </Label>
          <div className="mt-2 flex items-start gap-4 border border-white/10 bg-black p-4">
            <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center border border-white/10 bg-[#0d0d10]">
              {form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo"
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <ImageIcon
                  className="h-8 w-8 text-white/30"
                  strokeWidth={1.5}
                />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                data-testid="logo-file-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                onChange={uploadLogo}
                className="hidden"
              />
              <button
                type="button"
                data-testid="upload-logo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 bg-[#FF4B00] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300] disabled:opacity-40"
              >
                <Upload className="h-3.5 w-3.5" strokeWidth={2.5} />
                {uploading
                  ? "Wird hochgeladen..."
                  : form.logo_url
                    ? "Logo ersetzen"
                    : "Logo hochladen"}
              </button>
              {form.logo_url && (
                <button
                  type="button"
                  data-testid="remove-logo-btn"
                  onClick={removeLogo}
                  className="mono ml-2 inline-flex items-center gap-1 border border-white/20 px-3 py-2 text-[10px] uppercase tracking-widest hover:border-[#EF4444] hover:text-[#EF4444]"
                >
                  <Trash2 className="h-3 w-3" />
                  Entfernen
                </button>
              )}
              <p className="mono mt-3 text-[10px] uppercase tracking-widest text-white/40">
                PNG, JPG, WEBP, SVG oder GIF · max. 2 MB
              </p>
              <p className="mono mt-1 text-[10px] uppercase tracking-widest text-white/40">
                Erscheint im Header, Footer und Login
              </p>
            </div>
          </div>
        </div>

        {/* Hero-Fahrzeug Upload */}
        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Hero-Fahrzeug (nur Auto, transparent)
          </Label>
          <div className="mt-2 flex items-start gap-4 border border-white/10 bg-black p-4">
            <div className="flex h-24 w-32 flex-shrink-0 items-center justify-center border border-white/10 bg-[#0d0d10]">
              {form.hero_car_url ? (
                <img
                  src={form.hero_car_url}
                  alt="Fahrzeug"
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <ImageIcon
                  className="h-8 w-8 text-white/30"
                  strokeWidth={1.5}
                />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={heroCarInputRef}
                data-testid="hero-car-file-input"
                type="file"
                accept="image/png,image/webp,image/svg+xml"
                onChange={uploadHeroCar}
                className="hidden"
              />
              <button
                type="button"
                data-testid="upload-hero-car-btn"
                onClick={() => heroCarInputRef.current?.click()}
                disabled={uploadingCar}
                className="inline-flex items-center gap-2 bg-[#FF4B00] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300] disabled:opacity-40"
              >
                <Upload className="h-3.5 w-3.5" strokeWidth={2.5} />
                {uploadingCar
                  ? "Wird hochgeladen..."
                  : form.hero_car_url
                    ? "Fahrzeug ersetzen"
                    : "Fahrzeug hochladen"}
              </button>
              {form.hero_car_url && (
                <button
                  type="button"
                  data-testid="remove-hero-car-btn"
                  onClick={removeHeroCar}
                  className="mono ml-2 inline-flex items-center gap-1 border border-white/20 px-3 py-2 text-[10px] uppercase tracking-widest hover:border-[#EF4444] hover:text-[#EF4444]"
                >
                  <Trash2 className="h-3 w-3" />
                  Entfernen
                </button>
              )}
              <p className="mono mt-3 text-[10px] uppercase tracking-widest text-white/40">
                Transparente PNG oder SVG · max. 4 MB
              </p>
              <p className="mono mt-1 text-[10px] uppercase tracking-widest text-white/40">
                Fährt animiert von links ins Hero-Bild ein und stoppt rechts
              </p>
              <p className="mono mt-1 text-[9px] uppercase tracking-widest text-white/30">
                Tipp: Auto mit remove.bg freistellen für beste Wirkung
              </p>
            </div>
          </div>
        </div>

        {/* Hero-Sound Upload */}
        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Hero-Sound (Motor-Geräusch)
          </Label>
          <div className="mt-2 flex items-start gap-4 border border-white/10 bg-black p-4">
            <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center border border-white/10 bg-[#0d0d10]">
              {form.hero_sound_url ? (
                <Volume2 className="h-8 w-8 text-[#FF4B00]" strokeWidth={1.5} />
              ) : (
                <VolumeX className="h-8 w-8 text-white/30" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex-1">
              <input
                ref={heroSoundInputRef}
                data-testid="hero-sound-file-input"
                type="file"
                accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/aac,audio/webm,.mp3,.wav,.ogg,.m4a,.aac"
                onChange={uploadHeroSound}
                className="hidden"
              />
              <button
                type="button"
                data-testid="upload-hero-sound-btn"
                onClick={() => heroSoundInputRef.current?.click()}
                disabled={uploadingSound}
                className="inline-flex items-center gap-2 bg-[#FF4B00] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300] disabled:opacity-40"
              >
                <Upload className="h-3.5 w-3.5" strokeWidth={2.5} />
                {uploadingSound
                  ? "Wird hochgeladen..."
                  : form.hero_sound_url
                    ? "Sound ersetzen"
                    : "Sound hochladen"}
              </button>
              {form.hero_sound_url && (
                <>
                  <button
                    type="button"
                    data-testid="remove-hero-sound-btn"
                    onClick={removeHeroSound}
                    className="mono ml-2 inline-flex items-center gap-1 border border-white/20 px-3 py-2 text-[10px] uppercase tracking-widest hover:border-[#EF4444] hover:text-[#EF4444]"
                  >
                    <Trash2 className="h-3 w-3" />
                    Entfernen
                  </button>
                  <div className="mt-3">
                    <audio
                      data-testid="hero-sound-preview"
                      src={form.hero_sound_url}
                      controls
                      className="h-8 w-full max-w-[280px]"
                    />
                  </div>
                </>
              )}
              <p className="mono mt-3 text-[10px] uppercase tracking-widest text-white/40">
                MP3, WAV, OGG, M4A oder AAC · max. 5 MB
              </p>
              <p className="mono mt-1 text-[10px] uppercase tracking-widest text-white/40">
                Wird beim Öffnen der Seite abgespielt (User muss aktivieren)
              </p>
            </div>
          </div>
        </div>

        {/* Promo Sticker Toggle */}
        <div>
          <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
            Aktions-Sticker
          </Label>
          <div className="mt-2 flex items-center justify-between border border-white/10 bg-black p-4">
            <div>
              <div className="text-sm text-white">Sticker auf der Startseite anzeigen</div>
              <div className="mono mt-1 text-[10px] uppercase tracking-widest text-white/40">
                Ein orangefarbener Kreis-Sticker im Hero-Bereich mit Ihrer Aktion
              </div>
            </div>
            <Switch
              data-testid="promo-sticker-active"
              checked={!!form.promo_sticker_active}
              onCheckedChange={(v) =>
                setForm({ ...form, promo_sticker_active: v })
              }
            />
          </div>
        </div>

        {SETTINGS_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
              {f.label}
            </Label>
            {f.type === "textarea" ? (
              <Textarea
                data-testid={`settings-${f.key}`}
                className="mt-2 border-white/20 bg-black text-white"
                value={form[f.key] || ""}
                onChange={(e) =>
                  setForm({ ...form, [f.key]: e.target.value })
                }
              />
            ) : (
              <Input
                data-testid={`settings-${f.key}`}
                type={f.type}
                className="mt-2 border-white/20 bg-black text-white"
                value={form[f.key] || ""}
                onChange={(e) =>
                  setForm({ ...form, [f.key]: e.target.value })
                }
              />
            )}
            {f.hint && (
              <p className="mono mt-2 text-[10px] uppercase tracking-widest text-white/40">
                {f.hint}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          data-testid="settings-reset"
          onClick={() => setForm(current)}
          className="border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:border-white"
        >
          Zurücksetzen
        </button>
        <button
          data-testid="settings-save"
          onClick={save}
          disabled={saving}
          className="bg-[#FF4B00] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300] disabled:opacity-40"
        >
          {saving ? "Speichern..." : "Speichern"}
        </button>
      </div>
    </div>
  );
}

function ServicesView({ services, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SERVICE);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_SERVICE);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description,
      name_en: s.name_en || "",
      description_en: s.description_en || "",
      duration_minutes: s.duration_minutes,
      price_eur: s.price_eur,
      active: s.active,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        duration_minutes: parseInt(form.duration_minutes, 10),
        price_eur: parseFloat(form.price_eur),
      };
      if (editing) {
        await api.put(`/admin/services/${editing.id}`, payload);
        toast.success("Leistung aktualisiert");
      } else {
        await api.post("/admin/services", payload);
        toast.success("Leistung angelegt");
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Leistung wirklich löschen?")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Gelöscht");
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-white/60">
          Verwalten Sie die auf Ihrer Website buchbaren Leistungen.
        </p>
        <button
          data-testid="add-service-btn"
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#FF4B00] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300]"
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} /> Neue Leistung
        </button>
      </div>

      <div className="grid gap-px bg-white/10 md:grid-cols-2 lg:grid-cols-3">
        {services.map((s) => (
          <div
            key={s.id}
            data-testid={`service-row-${s.id}`}
            className="bg-[#0d0d10] p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-black uppercase">{s.name}</h3>
                <div className="mono mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {s.duration_minutes} Min · {s.price_eur.toFixed(2).replace(".", ",")} €
                </div>
              </div>
              <span
                className={`mono border px-2 py-0.5 text-[9px] uppercase tracking-widest ${s.active ? "border-[#10B981] text-[#10B981]" : "border-white/20 text-white/40"}`}
              >
                {s.active ? "Aktiv" : "Inaktiv"}
              </span>
            </div>
            <p className="mt-3 text-sm text-white/60">{s.description}</p>
            <div className="mt-5 flex gap-2 border-t border-white/10 pt-4">
              <button
                data-testid={`edit-service-${s.id}`}
                onClick={() => openEdit(s)}
                className="mono inline-flex items-center gap-1 border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-widest hover:border-white/40"
              >
                <Edit3 className="h-3 w-3" /> Bearbeiten
              </button>
              <button
                data-testid={`delete-service-${s.id}`}
                onClick={() => del(s.id)}
                className="mono inline-flex items-center gap-1 border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-widest text-white/60 hover:border-[#EF4444] hover:text-[#EF4444]"
              >
                <Trash2 className="h-3 w-3" /> Löschen
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="service-dialog"
          className="border-white/10 bg-[#0d0d10]"
        >
          <DialogHeader>
            <DialogTitle className="font-black uppercase">
              {editing ? "Leistung bearbeiten" : "Neue Leistung"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                Name
              </Label>
              <Input
                data-testid="service-name"
                className="mt-2 border-white/20 bg-black text-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                Beschreibung
              </Label>
              <Textarea
                data-testid="service-description"
                className="mt-2 border-white/20 bg-black text-white"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <div className="rounded border border-dashed border-white/10 bg-black/30 p-4">
              <div className="mono mb-3 text-[10px] uppercase tracking-[0.2em] text-[#FF4B00]">
                Englische Version (optional)
              </div>
              <div>
                <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                  Name (English)
                </Label>
                <Input
                  data-testid="service-name-en"
                  className="mt-2 border-white/20 bg-black text-white"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="z.B. Oil change"
                />
              </div>
              <div className="mt-3">
                <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                  Description (English)
                </Label>
                <Textarea
                  data-testid="service-description-en"
                  className="mt-2 border-white/20 bg-black text-white"
                  value={form.description_en}
                  onChange={(e) =>
                    setForm({ ...form, description_en: e.target.value })
                  }
                  placeholder="English description – leave empty to use the German version"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                  Dauer (Min)
                </Label>
                <Input
                  data-testid="service-duration"
                  type="number"
                  className="mono mt-2 border-white/20 bg-black text-white"
                  value={form.duration_minutes}
                  onChange={(e) =>
                    setForm({ ...form, duration_minutes: e.target.value })
                  }
                />
              </div>
              <div>
                <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                  Preis (€)
                </Label>
                <Input
                  data-testid="service-price"
                  type="number"
                  step="0.01"
                  className="mono mt-2 border-white/20 bg-black text-white"
                  value={form.price_eur}
                  onChange={(e) =>
                    setForm({ ...form, price_eur: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="service-active"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
              <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                Aktiv (auf Website sichtbar)
              </Label>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              className="border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-white"
            >
              Abbrechen
            </button>
            <button
              data-testid="service-save"
              onClick={save}
              className="bg-[#FF4B00] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E64300]"
            >
              Speichern
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
